/**
 * Installer-side service catalog URL builders, and the routing decision
 * between them.
 *
 * Extracted from `newServiceForm.jsx` (Task 9, template pricing overrides) so
 * the single most price-sensitive decision in this repo — which catalog a
 * job is priced from — has a harness (`scripts/test/serviceCatalogUrl.test.mjs`)
 * instead of living only inside a React component's `componentDidUpdate`.
 *
 * ZERO IMPORTS BY DESIGN, except `catalogFilterParams` from the sibling
 * zero-import module `marineService.js` — same pattern `catalogReadiness.js`
 * already uses. Do not add a React/lodash/axios import here.
 *
 * See `docs/DATA_CONVENTIONS.md` §4.bb for the resolution this module's
 * routing exists to reach: `COALESCE(dealer_template, mobile_template,
 * trim_override, base)`, evaluated server-side, per `(master_service,
 * vehicle_size, catalog)`.
 */
import { catalogFilterParams } from './marineService.js';

const catalogBase = (installerId) => `/api/installer-services/installer/${installerId}`;

/**
 * Build a query string, OMITTING any param whose value is undefined, null or
 * an empty string — never serializing one as the literal string "undefined".
 * `/template` makes `catalog`, `installerCategory` and a size selector
 * MANDATORY where the plain read does not; a stringified "undefined" there
 * is a 400 (or worse, a literal string the server tries to match on), not a
 * no-op.
 */
function buildQuery(fields) {
  const query = new URLSearchParams();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.set(key, value);
  });
  return query;
}

/**
 * A real, positive, numeric dealer id — not merely truthy. `addDealer`
 * (`newCustomerForm.jsx`) can hand back an object instead of an id on a
 * response-shape change (`response.dealerId || response`), and an object (or
 * a single-element array, which `Number()` happily coerces to its element)
 * truthiness-passes but serializes as `[object Object]` or otherwise isn't a
 * real id. Restricting to `number`/`string` before the `Number()` check is
 * what catches those, not just `Number.isFinite` on its own.
 */
export function isValidDealerId(dealerId) {
  if (typeof dealerId !== 'number' && typeof dealerId !== 'string') return false;
  if (dealerId === '') return false;
  const n = Number(dealerId);
  return Number.isFinite(n) && n > 0;
}

/**
 * Full, untemplated catalog for an installer/specialist — no dealer or
 * mobile-location filtering. Used by: a RETAIL estimate at a fixed location,
 * and the catalog admin screen (which must NEVER move to `/template`: it
 * deliberately reads with no size and no catalog, both mandatory there).
 */
export function buildInstallerServicesUrl(installerId, params = {}) {
  const query = buildQuery({
    ...catalogFilterParams(params),
    catalog: params.catalog,
    installerCategory: params.installerCategory,
    trimId: params.trimId,
  });
  return `${catalogBase(installerId)}?${query}`;
}

/**
 * The `/template` endpoint. `dealerId` and `locationId` are independent
 * filters the server evaluates separately and resolves per cell — confirmed
 * against `getWithTemplateFilteringImpl` in JobviousServer, which destructures
 * both from its options and pushes their filter clauses independently
 * (`if (dealerId) {...}` then `if (applyMobileFilter) {...}`). Passing BOTH
 * is what keeps a wholesale job AT a mobile location on the Mobile template's
 * surcharge/restriction for every cell the dealer's own template doesn't
 * override — omitting `locationId` for that case silently drops the Mobile
 * leg entirely, not just its precedence.
 *
 * Pass whichever of `dealerId` / `locationId` applies; either, both, or
 * neither. Omit `trimId` and the join finds nothing and every row falls back
 * to its base price (§4.x) — same hazard as the plain read.
 */
export function buildDealerTemplateUrl(installerId, params = {}) {
  const query = buildQuery({
    ...catalogFilterParams(params),
    catalog: params.catalog,
    installerCategory: params.installerCategory,
    dealerId: params.dealerId,
    locationId: params.locationId,
    trimId: params.trimId,
  });
  return `${catalogBase(installerId)}/template?${query}`;
}

/**
 * Retail estimate at a mobile location — locationId only, no dealer
 * identity. Thin, readable wrapper; delegates to `buildDealerTemplateUrl`
 * with `dealerId` omitted so there is exactly one query-building
 * implementation for `/template`.
 */
export function buildMobileLocationTemplateUrl(installerId, params = {}) {
  return buildDealerTemplateUrl(installerId, { ...params, dealerId: undefined });
}

/**
 * Decide which installer-side catalog URL to fetch, and build it. Single
 * source of truth for the routing table Task 9 introduced.
 *
 * `params.catalog` doubles as the wholesale/retail signal — it is already
 * `_.upperCase(customerType)`, so `'WHOLE'` IS "this is a wholesale
 * customer"; no separate flag is threaded through.
 *
 * `params.dealerId` is trusted only when `isValidDealerId` passes. A
 * wholesale customer with no valid dealer id is a wiring gap (every
 * wholesale-customer path threads `dealerId` alongside `customerType`), not
 * a valid state — it degrades to the SAME route a retail customer would take
 * (mobile-template-or-plain) rather than sending a malformed `/template`
 * request, and is reported back via `wholesaleIdMissing` so the caller can
 * surface it (this repo's callers `alert`, since a silently-dropped
 * dealership price book is a money bug, not a cosmetic one).
 *
 * `params.locationId` should be the MOBILE location's id, or omitted/falsy
 * for a fixed one — the server derives mobile-ness from the location row, so
 * sending it for a fixed location is a harmless no-op, but callers should
 * still only pass it when the location actually is mobile, to keep the
 * request honest about what it's asking for.
 */
export function resolveInstallerCatalogUrl(installerId, params = {}) {
  const { catalog, dealerId, locationId, ...rest } = params;
  const isWholesale = catalog === 'WHOLE';
  const dealerIdApplied = isWholesale && isValidDealerId(dealerId);
  const wholesaleIdMissing = isWholesale && !dealerIdApplied;
  const usesTemplate = dealerIdApplied || Boolean(locationId);

  const url = usesTemplate
    ? buildDealerTemplateUrl(installerId, {
        ...rest,
        catalog,
        dealerId: dealerIdApplied ? dealerId : undefined,
        locationId: locationId || undefined,
      })
    : buildInstallerServicesUrl(installerId, { ...rest, catalog });

  return { url, dealerIdApplied, wholesaleIdMissing };
}
